<template>
  <v-container grid-list-lg>
    <v-row>
      <v-col cols="12">
        <h1
          v-if="isNew"
          mt-10
        >
          <v-icon color="secondary">
            {{ $appConfig.dataset.icon }}
          </v-icon> Dataset
        </h1>
        <!-- form START -->
        <form>
          <!-- name field START -->
          <v-text-field
            v-model="form.name"
            v-validate="{ regex: /^(([a-zA-Z]\w*)[^_])$/, required: true}"
            label="Name"
            data-vv-name="name"
            :error-messages="errors.collect('name')"
            :disabled="creationLoadingButton || !isNew"
            @input="handleInputName"
          />
          <!-- name field END -->

          <!-- description field START -->
          <v-textarea
            v-model="form.description"
            v-validate=""
            label="Description"
            :error-messages="errors.collect('description')"
            data-vv-name="description"
            outlined
            rows="2"
            :disabled="creationLoadingButton"
          />
          <!-- description field END -->

          <!-- labels START -->
          <hmd-label
            :curr-labels="currLabels"
            :items="isNew ? labels : labelsProp"
            @labels="updateLabels"
          />
          <!-- labels END -->

          <!-- read only START -->
          <v-checkbox
            v-model="readOnlyCheckBox"
            color="primary"
            :disabled="creationLoadingButton"
          >
            <div slot="label">
              Read only
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-icon
                    small
                    v-on="on"
                  >
                    mdi-help-circle
                  </v-icon>
                </template>
                <span>If checked, disable the dataset manipulation from the website</span>
              </v-tooltip>
            </div>
          </v-checkbox>
          <!-- read only END -->

          <!-- privacy switch START -->
          <v-row align="center">
            <v-col
              class="mt-4"
              cols="2"
            >
              Privacy
            </v-col>
            <v-col
              class="mt-4"
              cols="1"
            >
              <v-switch
                v-model="form.isPrivate"
                color="primary"
                :disabled="creationLoadingButton"
              />
            </v-col>
            <v-col
              class="mt-4"
              cols="4"
            >
              <v-icon
                large
                :color="form.isPrivate ? 'primary' : 'grey'"
              >
                {{ form.isPrivate ? 'mdi-lock' : 'mdi-earth' }}
              </v-icon> {{ form.isPrivate ? 'Private mode' : 'Public mode' }}
            </v-col>
          </v-row>
          <!-- privacy switch END -->

          <!-- upload method START -->
          <div
            v-if="isNew"
            class="text-center"
          >
            <v-btn
              color="secondary"
              :flat="uploadMethod === 'fileUpload' ? false : true"
              :disabled="isSchema"
              @click="uploadMethod='fileUpload'"
            >
              <v-icon color="primary">
                mdi-content-save-outline
              </v-icon>
              &nbsp;&nbsp;Upload file
            </v-btn>
            <span
              color="secondary"
              style="font-size:20px;"
              class="no-select"
            >|</span>
            <v-btn
              color="secondary"
              :flat="uploadMethod === 'connectors' ? false : true"
              :disabled="isSchema"
              @click="uploadMethod='connectors'"
            >
              <v-icon color="primary">
                mdi-cloud-download
              </v-icon>
              &nbsp;&nbsp;Connectors
            </v-btn>
          </div>
          <!-- upload method END -->

          <!-- if in new dataset START -->
          <v-row
            v-if="isNew"
            align="center"
          >
            <!-- file uploader START -->
            <v-col
              v-if="(isInitial || isSaving) && uploadMethod === 'fileUpload' && worksheets.list.length<=0"
              cols="12"
            >
              <div>
                <form
                  v-if="isInitial || isSaving"
                  enctype="multipart/form-data"
                  novalidate
                >
                  <div class="dropbox grey lighten-4">
                    <input
                      ref="fileUploadInput"
                      type="file"
                      :multiple="false"
                      :name="uploadFieldName"
                      :disabled="isSaving"
                      accept=".csv, .CSV,, .json, .JSON, .xlsx, .xls"
                      class="input-file"
                      @change="onFileChange($event.target.name, $event.target.files); fileCount = $event.target.files.length"
                    >
                    <p v-if="isInitial">
                      Drag your excel, csv or json file here to begin<br> or click to browse
                    </p>
                    <p v-if="isSaving">
                      Uploading {{ fileCount }} files...
                    </p>
                  </div>
                </form>
              </div>
            </v-col>

            <!-- file uploader END -->

            <!-- Connectors upload START -->
            <template v-else-if="(isInitial || isSaving) && uploadMethod === 'connectors'">
              <v-container
                v-if="!isConnectorForm"
                class="text-center cols sm md lg"

                grid-list-lg
              >
                <v-text-field
                  v-model="searchConnector"
                >
                  <template slot="label">
                    <v-icon>search</v-icon>Search
                  </template>
                </v-text-field>
                <v-row
                  class="mt-3"
                >
                  <v-col
                    v-for="connector in connectors"
                    :key="connector.id"
                    cols="12"
                    sm="6"
                    md="4"
                    lg="2"
                  >
                    <v-card
                      flat
                      height="100%"
                      class="pa-2 text-left connector-card no-select"
                      :class="{ 'selected' : selectedConnector.id === connector.id }"
                      @click.native="selectedConnector = JSON.parse(JSON.stringify(connector)); addFieldsConnector()"
                    >
                      <!-- connector logo and name START -->
                      <v-card-media
                        class="mt-2"
                        :src="connector.logo"
                        :height="`${ selectedConnector === connector ? '46px' : '50px'}`"
                        contain
                      />
                      <v-card-text
                        primary-title
                        class="pa-0 text-center"
                      >
                        <div class="mt-3 mb-1">
                          <h4>{{ connector.name }}</h4>
                        </div>
                      </v-card-text>
                      <!-- connector logo and name END -->
                    </v-card>
                  </v-col>
                </v-row>
              </v-container>

              <!-- singer combobox form START -->
              <!-- <v-container v-else-if="!!selectedConnector.singer" fill-height grid-list-lg> -->
              <v-container
                v-else-if="isSingerForm"
                class="fill-height"
                grid-list-lg
              >
                <v-row
                  justify="center"
                >
                  <v-col
                    cols="12"
                    sm="8"
                    md="6"
                    lg="6"
                    class="text-center"
                  >
                    <h2>
                      <v-card-media
                        class="mt-2"
                        :src="selectedConnector.logo"
                        height="20px"
                        contain
                      />
                      {{ selectedConnector.name }}
                    </h2>
                    <v-col cols="12" />
                    <v-col cols="12" />
                    <v-form>
                      <v-row>
                        <v-col cols="12">
                          <v-list
                            v-for="(dataset, index) in singerDatasets"
                            :key="index"
                          >
                            <v-list-tile-content>
                              <v-checkbox
                                v-model="singerCheckboxes[index].checked"
                                :label="dataset"
                              />
                              <v-radio-group
                                v-if="singerCheckboxes[index].checked"
                                v-model="singerRadioGroups[index]"
                                row
                              >
                                <v-radio
                                  label="Full table"
                                  value="full_table"
                                />
                                <v-radio
                                  label="Customize"
                                  value="customize"
                                  @change="handleClickCustomize(dataset)"
                                />
                              </v-radio-group>
                            </v-list-tile-content>
                          </v-list>
                        </v-col>
                      </v-row>
                    </v-form>
                  </v-col>
                </v-row>
              </v-container>
              <!-- singer combobox form END -->

              <!-- connector form START -->
              <v-container
                v-else-if="isSchemaEmpty && worksheets.list.length<=0"
                class="fill-height"
                grid-list-lg
              >
                <v-row column>
                  <v-row
                    justify="center"
                  >
                    <v-col
                      cols="12"
                      sm="8"
                      md="6"
                      lg="6"
                      class="text-center"
                    >
                      <h2>
                        <v-card-media
                          class="mt-2"
                          :src="selectedConnector.logo"
                          height="20px"
                          contain
                        />
                        {{ selectedConnector.name }}
                      </h2>
                      <v-col cols="12" />
                      <v-col cols="12" />
                      <v-form>
                        <v-row>
                          <v-col
                            v-for="(el, index) in selectedConnector.config"
                            :key="index"
                            cols="12"
                          >
                            {{ el.label }}<span style="color:red">{{ (!!el.required ? ' *' : '') }}</span>
                            <v-text-field
                              v-model="el.value"
                              v-validate="{rules : { required: !!el.required}}"
                              :disabled="el.readonly"
                              :placeholder="el.placeholder"
                              solo
                              :data-vv-name="el.name"
                              :data-vv-scope="selectedConnector.name"
                            />
                            <p
                              v-if="errors.has(selectedConnector.name + '.' + el.name + ':required')"
                              class="text-error"
                            >
                              {{ errors.first(selectedConnector.name + '.' + el.name + ':required') }}
                            </p>
                          </v-col>
                        </v-row>
                      </v-form>
                    </v-col>
                  </v-row>
                </v-row>
              </v-container>
              <v-row
                v-if="isSchemaEmpty && worksheets.list.length<=0"
                align="center"
              >
                <v-col
                  cols="12"
                  class="text-center"
                >
                  <v-btn
                    flat
                    :disabled="!isConnectorForm ||isSaving"
                    @click="handleClickPreviousButton"
                  >
                    <v-icon>chevron_left</v-icon>
                    Previous
                  </v-btn>

                  <v-btn
                    v-if="!isConnectorForm || !isSingerForm && selectedConnector.labels.includes('singer')"
                    flat
                    :disabled="!selectedConnector"
                    @click="handleClickNextButton"
                  >
                    Next
                    <v-icon>chevron_right</v-icon>
                  </v-btn>

                  <v-btn
                    v-if="isConnectorForm && !isSingerForm && !selectedConnector.labels.includes('singer') || isSingerForm && isConnectorForm"
                    flat
                    :disabled="incorrectConnectorForm || isSaving"
                    @click="handleClickCallButton"
                  >
                    Call&nbsp;&nbsp;&nbsp;
                    <v-icon>send</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </template>
            <!-- Connector uploader END -->

            <!-- schema fields edition START -->
            <v-col
              v-else-if="isSchema"
              cols="12"
              class="grey lighten-4 text-right pa-3 dataset-configure-fields"
            >
              <!-- advanced edition switch START -->
              <v-switch
                v-model="advancedEdition"
                color="primary"
                label="Advanced"
                style="position: absolute; margin-top: 6px; margin-left: 3%;"
              />
              <!-- advanced edition switch END -->

              <!-- change file and reset buttons START -->
              <v-btn
                flat
                :disabled="creationLoadingButton"
                @click.native="function(){worksheets.list=[];worksheets.selected=-1;form.name=''; currentStatus=0; isSchema=false; dataset.fields=[]; dataset.updatedFields=[];}"
              >
                {{ isFailed ? 'New try' : 'Change file' }}
              </v-btn>
              <v-btn
                v-show="!isFailed"
                flat
                :disabled="creationLoadingButton"
                @click.native="resetFile"
              >
                Reset
              </v-btn>
              <!-- change file and reset buttons END -->

              <!-- primary key messages START -->
              <v-col
                v-if="!primaryKeyPresent && !!this.primaryKeyWarning && !isFailed"
                cols="8"
                offset="2"
              >
                <div class="text-center warning--text">
                  <v-icon
                    x-large
                    color="warning"
                  >
                    warning
                  </v-icon><v-spacer />
                  {{ this.primaryKeyWarning }}
                </div>
              </v-col>
              <v-col
                v-if="!!this.primaryKeyError && !isFailed"
                cols="8"
                offset="2"
              >
                <div class="text-center error--text">
                  <v-icon
                    x-large
                    color="red"
                  >
                    warning
                  </v-icon><v-spacer />
                  {{ this.primaryKeyError }}
                </div>
              </v-col>
              <v-col
                v-if="isFailed"
                cols="8"
                offset="2"
              >
                <div class="text-center error--text">
                  <v-icon
                    x-large
                    color="red"
                  >
                    warning
                  </v-icon><v-spacer />
                  Something went wrong<br>
                  Check the configuration or the state of the server.
                </div>
              </v-col>
              <!-- primary key messages START -->

              <!-- schema fields table START -->
              <div
                v-if="isSchema"
                class="function-response"
              >
                <v-data-table
                  :headers="this.headers"
                  :items="dataset.updatedFields"
                  hide-default-footer
                  class="elevation-1"
                >
                  <template
                    slot="items"
                    slot-scope="props"
                  >
                    <td
                      v-if="advancedEdition"
                      align="center"
                      width="80px"
                      :class="{ 'pending-delete': !props.item.exists}"
                    >
                      <span v-if="props.item.possible_pk">
                        <v-tooltip
                          class="no-select"
                          top
                        >
                          <template v-slot:activator="{ on }">
                            <v-icon
                              large
                              v-on="on"
                            >
                              edit_attributes
                            </v-icon>
                          </template>
                          <span>Eligible as primary key</span>
                        </v-tooltip>
                      </span>
                    </td>

                    <td :class="{ 'pending-delete': !props.item.exists }">
                      <v-edit-dialog
                        :return-value.sync="props.item.updatedName"
                        lazy
                      >
                        {{ props.item.updatedName }}
                        <v-text-field
                          slot="input"
                          v-model="props.item.updatedName"
                          label="Edit"
                          single-line
                          counter
                          :disabled="creationLoadingButton"
                          @focus="$event.target.select()"
                          @change="checkName(props.item)"
                        />
                      </v-edit-dialog>
                    </td>

                    <td :class="{ 'pending-delete': !props.item.exists }">
                      <v-select
                        v-model="props.item.type"
                        :items="dataTypes"
                        :disabled="props.item.primary_key === true && !props.item.isNew || creationLoadingButton"
                      />
                    </td>

                    <template v-if="advancedEdition">
                      <td :class="{ 'pending-delete': !props.item.exists }">
                        <v-checkbox
                          v-model="props.item.primary_key"
                          color="primary"
                          :disabled="creationLoadingButton"
                          @change="e=>{if(e) {dataset.updatedFields[props.index].required=true}}"
                        />
                      </td>
                      <td
                        v-if="dataset.updatedFields.find(o=>{return o.possible_required})"
                        :class="{ 'pending-delete': !props.item.exists }"
                      >
                        <v-checkbox
                          v-model="props.item.required"
                          color="primary"
                          :disabled="creationLoadingButton || !props.item.possible_required || props.item.primary_key"
                          @change="e=>{if(!e && props.item.primary_key) props.item.primary_key=false}"
                        />
                      </td>

                      <td
                        class="text-center"
                        :class="{ 'pending-delete': !props.item.exists }"
                      >
                        <v-btn
                          flat
                          icon
                          small
                          :disabled="creationLoadingButton"
                          @click.native="addColumn(props.item)"
                        >
                          <v-icon>add</v-icon>
                        </v-btn>
                        <v-btn
                          flat
                          icon
                          small
                          :disabled="creationLoadingButton"
                          @click.native="deleteColumn(props.item)"
                        >
                          <v-icon v-if="props.item.exists">
                            mdi-delete
                          </v-icon>
                          <v-icon v-else>
                            delete_forever
                          </v-icon>
                        </v-btn>
                      </td>
                    </template>
                  </template>
                </v-data-table>
                <v-row
                  v-show="uploadMethod === 'connectors'"
                  align="center"
                >
                  <v-checkbox
                    v-model="scheduleBoxChecked"
                    color="primary"
                    label="Recreate this dataset every "
                    hide-details
                    class="shrink mr-2"
                    style="min-width: 135px; margin-left: 15px;"
                    :disabled="creationLoadingButton"
                  />
                  <v-select
                    v-model="frequency"
                    :items="['day', 'week', 'month']"
                    :disabled="creationLoadingButton"
                    @change="scheduleBoxChecked = true"
                  />
                  &nbsp;&nbsp;&nbsp;
                  <template v-if="frequency === 'week'">
                    <v-select
                      v-model="selectedDay"
                      :items="['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday','Sunday']"
                      label="on"
                      :disabled="creationLoadingButton"
                      @change="scheduleBoxChecked = true"
                    />
                  </template>
                  <template v-if="frequency === 'month'">
                    <v-select
                      v-model="selectedDate"
                      :items="daysOfMonth()"
                      label="on the"
                      :disabled="creationLoadingButton"
                      @change="scheduleBoxChecked = true"
                    />
                  </template>
                  &nbsp;&nbsp;at&nbsp;&nbsp;&nbsp;
                  <v-text-field
                    v-model="hourInput"
                    v-validate="`${scheduleBoxChecked ? 'min_value:0|max_value:23|required' : ''}`"
                    label="hh"
                    style="width: 80px"
                    data-vv-name="hour"
                    :error-messages="errors.collect('hour')"
                    :disabled="creationLoadingButton"
                    @input="scheduleBoxChecked = true"
                  />
                  &nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;
                  <v-text-field
                    v-model="minInput"
                    v-validate="`${scheduleBoxChecked ? 'min_value:0|max_value:59|required' : ''}`"
                    label="mm"
                    style="width: 80px;"
                    data-vv-name="minutes"
                    :error-messages="errors.collect('minutes')"
                    :disabled="creationLoadingButton"
                    @input="scheduleBoxChecked = true"
                  />
                  <v-container fluid>
                    <v-radio-group
                      v-model="radioGroupStrategy"
                      style="padding-top:20px;"
                      row
                      :disabled="creationLoadingButton"
                      @change="scheduleBoxChecked=true"
                    >
                      <v-radio
                        label="renew"
                        value="renew"
                        color="primary"
                        @click="radioGroupStrategy='renew';scheduleBoxChecked = true"
                      />
                      <v-tooltip
                        class="no-select"
                        top
                        style="margin-top:-2px; margin-left:-12px; padding-right:25px;"
                      >
                        <template v-slot:activator="{ on }">
                          <v-icon
                            small
                            v-on="on"
                          >
                            mdi-help-circle
                          </v-icon>
                        </template>
                        <span>Truncate and insert imported data</span>
                      </v-tooltip>
                      <v-radio
                        label="update"
                        value="update"
                        color="primary"
                        @click="radioGroupStrategy='update';scheduleBoxChecked = true"
                      />
                      <v-tooltip
                        class="no-select"
                        top
                        style="margin-top:-2px; margin-left:-12px;"
                      >
                        <template v-slot:activator="{ on }">
                          <v-icon
                            small
                            v-on="on"
                          >
                            mdi-help-circle
                          </v-icon>
                        </template>
                        <span>Insert new rows and update modified ones</span>
                      </v-tooltip>
                    </v-radio-group>
                  </v-container>
                </v-row>
              </div>
              <!-- schema fields table END -->
            </v-col>
            <!-- schema fields edition END -->
            <v-col
              v-if="worksheets.list.length>0 && worksheets.createdBy==uploadMethod && isSchemaEmpty "
              cols="12"
            >
              <template>
                <v-row style="margin:25px 0px;">
                  <v-col
                    class="font-weight-light"
                    style="font-size:25px;"
                  >
                    Select the worksheet you want to import
                  </v-col>
                  <v-spacer />
                  <v-btn
                    flat
                    :disabled="creationLoadingButton"
                    @click.native="worksheets.list = [];worksheets.selected=-1;form.name=''; currentStatus=0; isSchema=false; dataset.fields=[]; dataset.updatedFields=[];"
                  >
                    {{ isFailed ? 'New try' : 'Change file' }}
                  </v-btn>
                  <v-btn
                    color="primary"
                    @click="nextAfterWorksheetSelection"
                  >
                    Next
                  </v-btn>
                </v-row>

                <v-tabs
                  v-model="worksheets.selected"
                  slider-color="teal"
                  color="grey lighten-4"
                >
                  <v-tab
                    v-for="(s,i) in worksheets.list"
                    :key="i"
                    active-class="v-tabs__item--active light-blue  white-font"
                  >
                    <v-icon>insert_drive_file</v-icon> &nbsp;&nbsp;{{ s.name }}
                  </v-tab>

                  <v-tab-item
                    v-for="(s,i) in worksheets.list"
                    :key="i"
                    :transition="false"
                    :reverse-transition="false"
                  >
                    <v-card
                      flat
                      style="padding:0px;"
                    >
                      <v-data-table
                        :headers="this._.map(s.data[0],(o,oi)=>{ return {text:oi,name:oi,align:'left',sortable:false}})"
                        :items="s.data"
                        class="elevation-1"
                      >
                        <template v-slot:items="props">
                          <td
                            v-for="(col,coli) in s.data[0]"
                            :key="coli"
                          >
                            {{ props.item[coli] }}
                          </td>
                        </template>
                      </v-data-table>
                    </v-card>
                  </v-tab-item>
                </v-tabs>
              </template>
            </v-col>
          </v-row>
          <!-- if in new dataset END -->

          <!-- edit/create buttons START -->
          <v-row align="center">
            <v-col
              cols="12"
            >
              <!-- We disable the button in 4 cases :
              - We are already creating the dataset
              - There is an error in the form (i.e. the dataset name is already used)
              - We want to create a new dataset, and at the step of the schema edition, the user only chosed one key that cannot be a primary key by itself
              - We are importing a dataset -->
              <v-btn
                :loading="creationLoadingButton"
                :disabled="creationLoadingButton || errors.any() || isNew && isSchema && !!primaryKeyError || isSaving"
                block
                color="primary"
                large
                @click="submit"
              >
                <v-icon
                  v-if="!isNew"
                  left
                >
                  mdi-pencil
                </v-icon>
                <v-icon
                  v-else
                  left
                >
                  create
                </v-icon>
                {{ isNew ? isSchema && !isSchemaEmpty || !isSchema && isSaving ? "Create dataset" : "Create empty dataset" : "Update dataset" }}
                <span slot="loader">Creating dataset...</span>
              </v-btn>
              <v-dialog
                v-model="creationDialog"
                max-width="400px"
              >
                <v-card>
                  <v-card-title class="headline">
                    Dataset creation
                  </v-card-title>
                  <v-card-text>
                    Your dataset is being created, it may take a long time depending on the file size. We will redirect you when the creation is complete.
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer />
                    <v-btn
                      color="primary"
                      flat
                      @click="creationDialog = false"
                    >
                      Close
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </v-col>
            <!-- edit/create buttons END -->
            <!-- delete button START -->
            <template v-if="!isNew">
              <v-col cols="12">
                <v-btn
                  outlined
                  color="orange"
                  large
                  block
                  @click="truncateDataset"
                >
                  <v-icon
                    left
                    class="mr-1"
                  >
                    mdi-delete-outline
                  </v-icon>
                  Truncate dataset
                </v-btn>
              </v-col>
              <v-col cols="12">
                <v-btn
                  outlined
                  color="blue"
                  large
                  block
                  @click="cloneDataset"
                >
                  <v-icon
                    left
                    class="mr-1"
                  >
                    mdi-content-duplicate
                  </v-icon>
                  Clone dataset
                </v-btn>
              </v-col>
              <v-col
                cols="12"
              >
                <v-btn
                  outlined
                  color="error"
                  large
                  block
                  @click="deleteDataset"
                >
                  <v-icon
                    left
                    class="mr-1"
                  >
                    mdi-delete-outline
                  </v-icon>
                  Delete dataset
                </v-btn>
              </v-col>
            </template>
          <!-- delete button END -->
          </v-row>
        </form>
        <!-- form END -->
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import _ from 'lodash'

  import ProjectService from './../Projects/services/ProjectService'
  import DatasetService from './services/DatasetService'
  import SingerService from './services/SingerService'
  import HmdLabel from '../../../UIComponents/HmdLabel.vue'
  import HmdService from '../../../UIComponents/services/HmdService'
  import IntegrationService from '../Integrations/services/IntegrationService'
  import FunctionService from '../Functions/services/FunctionService'

  import { slugify } from '../../../../utils/common'
  import DatasetBrowserVue from './DatasetBrowser.vue'

  const STATUS_INITIAL = 0; const STATUS_SAVING = 1; const STATUS_SUCCESS = 2; const STATUS_FAILED = 3

  export default {
    components: {
      HmdLabel,
    },
    $_veeValidate: {
      validator: 'new',
    },
    props: {
      value: [Array, String],
      accept: {
        type: String,
        default: '*',
      },
      label: {
        type: String,
        default: 'Please choose...',
      },
      required: {
        type: Boolean,
        default: false,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      multiple: {
        type: Boolean, // not yet possible because of data
        default: false,
      },
      isNew: {
        type: Boolean,
        default: function () {
          return true
        },
      },
      dataset: {
        type: Object,
        default: function () {
          return {
            originalFields: [],
            updatedFields: [],

          }
        },
      },
      labelsProp: {
        type: Array,
        default: function () {
          return []
        },
      },
    },
    data: function () {
      return {
        // pkSelected: false,
        worksheets: {
          list: [],
          selected: -1,
          createdBy: '',
        },
        containsDuplicates: false,
        duplicatedName: {},
        fileInputData: new FormData(),
        form: {
          items: ['tous', 'private'],
          name: '',
          description: '',
          labels: [],
          isPrivate: true,

        },
        signinDictionary: {
          attributes: {
            email: 'E-mail Address',
            // custom attributes
          },
          custom: {
            name: {
              required: () => 'The dataset name cannot be empty',
              regex: () => 'Enter a valid dataset name. This can only contain alphanumeric and underscore characters, has to start with a letter and not to end with an uderscore.',
            },
            host: {
              required: () => 'The host name cannot be empty',
            },
            username: {
              required: () => 'The user name cannot be empty',
            },
            password: {
              required: () => 'The password cannot be empty',
            },
            filename: {
              required: () => 'The file name cannot be empty',
            },
            hour: {
              min_value: () => '',
              max_value: () => '',
            },
            minutes: {
              min_value: () => '',
              max_value: () => '',
            },
          },
        },
        uploadedFiles: [],
        uploadError: null,
        currentStatus: null,
        uploadFieldName: 'file',
        isSchema: false,
        filename: '',
        currLabels: [],
        deleteDialog: false,
        dataTypes: [
          { text: 'String', value: 'text' },
          { text: 'Boolean', value: 'boolean' },
          { text: 'Integer', value: 'int' },
          { text: 'Decimal', value: 'float' },
          { text: 'Date', value: 'date' },
          { text: 'UUID', value: 'uuid' },
          { text: 'UUID Date & Time', value: 'timeuuid' },
          { text: 'Date & Hour', value: 'timestamp' },
          { text: 'Coordinates (2D)', value: 'tuple<float,float>' },
          { text: 'Coordinates (3D)', value: 'tuple<float,float,float>' },
          { text: 'IP Address', value: 'inet' },
        ],
        creationLoadingButton: false,
        creationDialog: false,
        labels: [],
        uploadMethod: 'fileUpload',
        takenDatasetName: '',
        visiblePassword: false,
        scheduleBoxChecked: false,
        frequency: 'day',
        selectedDay: 'Monday',
        selectedDate: '01',
        hourInput: '00',
        minInput: '00',
        isConnectorForm: false,
        listConnectors: [],
        selectedConnector: '',
        searchConnector: '',
        selectedDatasetRadio: '',
        isSingerForm: false,
        singerCheckboxes: [],
        singerRadioGroups: [],
        singerDatasets: [],
        radioGroupStrategy: 'renew',
        readOnlyCheckBox: !!this.dataset.is_readonly,
        advancedEdition: false,
        primaryKeyWarning: '',
        primaryKeyError: '',
      }
    },
    computed: {
      isInitial () {
        return this.currentStatus === STATUS_INITIAL
      },
      isSaving () {
        return this.currentStatus === STATUS_SAVING
      },
      isSuccess () {
        return this.currentStatus === STATUS_SUCCESS
      },
      isFailed () {
        return this.currentStatus === STATUS_FAILED
      },
      connectors: function () {
        return this.listConnectors.filter(connector => {
          return connector.name.toLowerCase().includes(this.searchConnector.toLowerCase())
        })
      },
      incorrectConnectorForm: function () {
        let invalid = false

        this.selectedConnector.config.some(el => {
          if (el.required && !el.value) {
            invalid = true
            return true
          }
        })
        return (invalid || this.errors.any(this.selectedConnector.name))
      },
      primaryKeyPresent: function () {
        let possiblePk = false
        let numberPk = 0

        this.dataset.updatedFields.some(field => {
          if (field.primary_key && field.exists) {
            numberPk += 1
            possiblePk = numberPk > 1 || !!field.possible_pk

            return possiblePk
          }
        })

        // If no primary key is selected, inform the user that we are going to generate one for him.
        // Nothing is displayed if the schema is empty.
        if (numberPk === 0) {
          this.primaryKeyWarning = this.isSchemaEmpty ? '' : 'A primary key will be automatically generated since there is no primary key selected'
          this.primaryKeyError = ''
        // If only one is selected, remove all warning messages
        } else if (numberPk === 1) {
          this.primaryKeyWarning = ''
          this.primaryKeyError = ''
          // If the one selected is not a "suitable" primary key, display an error message to the user
          if (!possiblePk) {
            this.primaryKeyError = 'The primary key you selected is not suitable'
          }
        // If two keys or more are selected, we removed all warning and error messages and accept those as a composite primary key
        } else {
          this.primaryKeyWarning = ''
          this.primaryKeyError = ''
        }

        return possiblePk
      },
      // Returns the headers for the "schema field" table
      // More fields are pushed if the advancedEdition setting is set to true
      headers: function () {
        const headers = [
          { text: 'Column name', sortable: false, value: 'column_name', align: 'left' },
          { text: 'Column type', sortable: false, value: 'type', align: 'left' },
        ]

        if (this.advancedEdition) {
          headers.unshift({ test: 'Possible PK', sortable: false, value: 'possiblePk', align: 'center' })
          headers.push({ text: 'Primary key', sortable: false, value: 'primary', align: 'center' })
          if (this.dataset.updatedFields.find(o => { return o.possible_required })) { headers.push({ text: 'Required', sortable: false, value: 'required', align: 'center' }) }
          headers.push({ text: 'Actions', sortable: false, value: 'actions', align: 'center' })
        }

        return headers
      },
      // Check if the user manually deleted every column.
      // Refreshed during the schema edition when creating a new table.
      isSchemaEmpty: function () {
        let empty = true

        this.dataset.updatedFields.some(field => {
          if (field.exists) {
            empty = false
            return true
          }
        })

        return empty
      },
    },
    watch: {
      value (v) {
        this.filename = v
      },
    },
    mounted: function () {
      this.currentStatus = STATUS_INITIAL
      this.$validator.localize('fr', this.signinDictionary)
      this.filename = this.value
      this.form.name = this.dataset.name
      this.form.description = this.dataset.description
      this.form.labels = this.dataset.labels
      if (typeof this.dataset.is_private !== 'undefined') {
        this.form.isPrivate = this.dataset.is_private
      }
      if (!this.isNew) {
        var i
        for (i in this.form.labels) {
          this.currLabels.push(this.form.labels[i].id)
        }
      } else {
        HmdService.label_list()
          .then(result => {
            this.labels = result.data
          })
        this.readOnlyCheckBox = true
      }

      const t = this
      IntegrationService.listStorage(this, 0).then(function (response) {
        if (response.data.results.length) {
          t.listConnectors = response.data.results
        }
      })
    },
    methods: {
      updateLabels (labels) {
        this.form.labels = labels
      },
      nextAfterWorksheetSelection () {
        if (this.uploadMethod == 'connectors') { this.handleClickCallButton() } else { this.save(this.fileInputData, this.worksheets.selected) }
      },
      submit () {
        this.$validator.validateAll().then((result) => {
          if (result) {
            var dataset = {
              name: this.form.name,
              description: this.form.description,
              labels: this.form.labels,
              is_private: this.form.isPrivate,
              file_upload_id: this.dataset.file_upload_id,
              is_readonly: this.readOnlyCheckBox,
            }

            if (this.isNew) {
              if (this.dataset.updatedFields.length > 0 && !this.isSchemaEmpty) {
                dataset.fields = this.dataset.updatedFields
              }
              var item, regexp
              var res = true
              regexp = new RegExp('(^(([A-Za-zÀ-ÖØ-öø-ÿ][A-zÀ-ÖØ-öø-ÿ0-9_ \-]*)[^_|\-| ])$)')
              // console.log(dataset.fields)
              for (item in dataset.fields) {
                var field = dataset.fields[item]
                if (field.isNew) {
                  field.name = field.updatedName
                }
                // TODO: define if we need to use a regex for the name
                // var res = regexp.test(field.name)
                res = true
                if (!res && field.exists) {
                  // console.log(field.name + ' | ' + field.updatedName + ' | ' + field.isNew)
                  this.$store.commit('push', { msg: 'error "' + field.name + '". Field names cannot contain special characters, except hyphens, underscore and whitespaces, but these cannot end the name.', color: 'error' })
                  break
                }
              }
              // console.log(dataset.fields)

              if (this.uploadMethod === 'connectors' && !!this.scheduleBoxChecked) {
                if (this.frequency === 'day') {
                  dataset.schedule = {
                    frequency: 'day',
                    hour: this.hourInput,
                    minutes: this.minInput,
                  }
                } else if (this.frequency == 'week') {
                  dataset.schedule = {
                    frequency: this.frequency,
                    day: this.selectedDay,
                    hour: this.hourInput,
                    minutes: this.minInput,
                  }
                // frequency == 'month'
                } else {
                  dataset.schedule = {
                    frequency: this.frequency,
                    date: this.selectedDate,
                    hour: this.hourInput,
                    minutes: this.minInput,
                  }
                }

                dataset.schedule.strategy = this.radioGroupStrategy
              }

              if (res) {
                this.checkErrors()
                if (this.containsDuplicates === false) {
                  let nameUsed = false
                  const t = this

                  DatasetService.list(this, 0)
                    .then(function (response) {
                      response.data.results.some(elem => {
                        if (elem.name === t.form.name) {
                          nameUsed = true
                          t.takenDatasetName = elem.name
                          t.errors.add('name', 'Dataset already exists in your database')
                          t.$store.commit('push', { msg: 'Dataset ' + elem.name + ' already exists in your database', color: 'error' })
                          return true
                        }
                      })

                      if (!nameUsed) {
                        t.creationLoadingButton = true
                        t.creationDialog = true
                        DatasetService.create(dataset, t)
                      }
                    })
                }
              }
            } else {
              const t = this
              dataset.fields = this.dataset.fields
              DatasetService.update(dataset, this).then(function (response) {
                // The datasetUpdated event is catch by the Dataset component to set to the browser tab as the active one
                t.$emit('datasetUpdated')
              })
            }
          }
        })
      },
      async cloneDataset () {
        const self = this
        const projects = await ProjectService.list(this).then(r => {
          return _.map(r.data.results, 'name')
        })
        this.$store.commit('confirm', {
          message: 'Do you really want to clone this dataset ?',
          title: 'Warning',
          color: 'warning',
          form: {
            clone_name: {
              type: 'text',
              label: 'New dataset name',
              placeholder: "New dataset's name",
            },
            target_project: {
              type: 'select',
              values: projects,
              label: 'Target project',
              placeholder: 'Choose a target project',
              required: true,
            },
          },
          onCancel: () => {},
          onConfirm: (args) => {
            ProjectService.list(self).then((result) => {
              // Retrieve project id
              let myPg = _.pickBy(result.data.results, { name: args.target_project })
              if (Object.keys(myPg).length > 0) {
                myPg = myPg[Object.keys(myPg)[0]].id
                DatasetService.clone(self, self.dataset, myPg, args.clone_name)
              }
            })
          },
        })
      },
      truncateDataset () {
        const self = this
        this.$store.commit('confirm', {
          message: 'Do you really want to truncate this dataset ?',
          title: 'Warning',
          color: 'warning',
          onCancel: () => {},
          onConfirm: () => {
            DatasetService.truncate(self.dataset, self)
          },
        })
      },
      deleteDataset () {
        const self = this
        this.$store.commit('confirm', {
          message: 'Do you really want to delete this dataset ?',
          title: 'Warning',
          color: 'warning',
          onCancel: () => {},
          onConfirm: () => {
            DatasetService.delete(self.dataset, self).then(() => {
              self.$emit('delete')
              self.$emit('close')
            }).finally(() => {
              self.deleteDialog = false
            })
          },
        })
      },
      getFormData (files) {
        const data = new FormData();
        [...files].forEach(file => {
          data.append('data', file, file.name) // currently only one file at a time
        })
        return data
      },
      onFocus () {
        if (!this.disabled) {
          this.$refs.file.click()
        }
      },
      save (formData, worksheetIndex) {
        // upload data to the server
        this.currentStatus = STATUS_SAVING
        DatasetService.upload(formData, worksheetIndex).then(x => {
          if (this._.isArray(x.data)) {
            this.worksheets.list = x.data
            this.worksheets.selected = 0
            this.worksheets.createdBy = 'fileUpload'
          } else {
            this.currentStatus = STATUS_SUCCESS
            this.dataset.originalFields = x.data.fields
            this.dataset.file_upload_id = x.data.file_upload_id
            this.dataset.pk_detected = x.data.pk_detected
            var item
            for (item in this.dataset.originalFields) {
              this.dataset.originalFields[item].exists = true
              this.dataset.originalFields[item].updatedName = this.dataset.originalFields[item].name
            }
            this.dataset.updatedFields = JSON.parse(JSON.stringify(this.dataset.originalFields))
            this.isSchema = true
          }
        })
          .catch(err => {
            this.$store.commit('push', { msg: 'Error ' + err.response.data.error, color: 'error' })
            this.uploadError = err.response
            this.currentStatus = STATUS_FAILED
          })
      },
      onFileChange (fieldName, fileList) {
        // handle file changes
        this.fileInputData = new FormData()
        var fileName = fileList[0].name.toLowerCase()
        if (fileName.endsWith('.json') || fileName.endsWith('.csv') || fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
          if (!fileList.length) return

          // append the files to FormData
          Array
            .from(Array(fileList.length).keys())
            .map(x => {
              this.fileInputData.append(fieldName, fileList[x], fileList[x].name)
            })

          // Add filename to the Dataset name if not informed
          if (this.form.name == undefined || this.form.name == null || this.form.name == '') {
            this.form.name = slugify(fileName.substr(0, fileName.lastIndexOf('.')))
          }

          if (this.worksheets.selected >= 0) { this.save(this.fileInputData, this.worksheets.selected) } else { this.save(this.fileInputData) }

          // Force VeeValidate to reevaluate the "name" field
          this.$nextTick(() => {
            this.$validator.validate('name')
          })
        } else {
          this.$store.commit('push', { msg: 'File must be of type JSON, CSV or XLS/XLSX', color: 'error' })
        }
      },
      checkErrors () {
        var item

        // var ans=false
        // for (item in this.dataset.updatedFields){
        //   if(this.dataset.updatedFields[item].exists === true){
        //     ans = ans || this.dataset.updatedFields[item].primary_key
        //   }
        // }
        // this.pkSelected = ans || this.isSchema === false

        var existingValuesArray = this.dataset.updatedFields.filter(x => x.exists === true)
        var valueArr = existingValuesArray.map(function (item) { return item.updatedName })
        var t = this
        this.containsDuplicates = valueArr.some(function (item, idx) {
          if (valueArr.indexOf(item) != idx) {
            t.duplicatedName = { index: idx, value: item }
          }
          return valueArr.indexOf(item) != idx
        })

        // if(this.pkSelected === false){
        //   this.$store.commit('push', {msg: 'no primary key selected', color: 'error'})
        // }

        if (this.containsDuplicates === true) {
          this.$store.commit('push', { msg: 'the schema contains duplicate names: index=' + this.duplicatedName.index + ' with name "' + this.duplicatedName.value + '"', color: 'error' })
        }
      },
      addColumn (item) {
        var index = this.dataset.updatedFields.findIndex(function (el) {
          return el === item
        })
        this.dataset.updatedFields.splice(index + 1, 0, { name: 'column_name', primary_key: false, type: 'text', exists: true, updatedName: 'column_name', isNew: true })
      },
      deleteColumn (item) {
        var index = this.dataset.updatedFields.findIndex(function (el) {
          return el === item
        })
        this.dataset.updatedFields[index].exists = !this.dataset.updatedFields[index].exists
      },
      resetFile () {
        this.currentStatus = 2
        this.dataset.updatedFields = JSON.parse(JSON.stringify(this.dataset.originalFields))

        if (this.uploadMethod === 'connectors') {
          this.scheduleBoxChecked = false,
          this.frequency = 'day',
          this.selectedDay = 'Monday',
          this.selectedDate = '01',
          this.hourInput = '',
          this.minInput = '',
          this.selectedDatasetRadio = 'renew'
        }
      },
      checkName (item) {
        if (item.updatedName.length === 0) {
          this.$store.commit('push', { msg: 'column name cannot be empty', color: 'error' })
          item.updatedName = item.name
        }
      },
      handleInputName: function () {
        if (!this.form.name || !this.takenDatasetName) return

        // Needed to enable the "create dataset" button after a user tried to create
        // a dataset with a name already used by another one.
        if (this.form.name !== this.takenDatasetName) {
          let i = 0

          // If the name has been changed, removes all error "Dataset exists in your database" so the dataset can be created
          this.errors.items.forEach(el => {
            if (el.field === 'name' && el.msg === 'Dataset exists in your database') {
              this.errors.items.splice(i, 1)
            } else {
              i++
            }
          })
        }
      },
      handleClickPreviousButton: function () {
        // We only get back to the connectors selection menu if we are at the configuration fields form,
        // which requires to have isConnectorForm = true and isSingerForm = false.
        // isSingerForm is true on the dataset selection form for singer components and false the rest of the time.
        this.isConnectorForm = !(this.isConnectorForm && !this.isSingerForm)
        this.isSingerForm = false
      },
      handleClickNextButton: function () {
        if (this.isConnectorForm === true && this.selectedConnector.labels.includes('singer')) {
          const t = this

          // Call tap discover here, and retrieve the datasets we could use
          SingerService.discover(this.selectedConnector.name.toLowerCase()).then(function (response) {
            t.isSingerForm = true
            t.singerDatasets = response.data

            t.singerCheckboxes = t.singerDatasets.map(dataset => {
              t.singerRadioGroups.push('full_table')
              return {
                checked: false,
              }
            })
          }).catch(function (error) {
            console.log(error)
          })
        } else {
          this.isConnectorForm = true
        }
      },
      handleClickCallButton: function () {
        const t = this
        this.currentStatus = STATUS_SAVING

        if (!this.isSingerForm) {
          DatasetService.getDataFromStorage(this.selectedConnector, t.worksheets.selected).then(function (response) {
            console.log(response)
            // Removes the file extension name if there is one
            if (!t.form.name) {
              let filename = ''
              // Retrieve the filename value if described by the user
              t.selectedConnector.config.forEach(el => {
                if (el.name === 'filename') {
                  filename = el.value
                }
              })
              if (filename.split('.').length > 1) {
                t.form.name = filename.split('.').slice(0, -1).join('_')
              } else {
                t.form.name = filename
              }

              t.form.name = t.form.name.toLowerCase()

              // Force VeeValidate to reevaluate the "name" field
              t.$nextTick(() => {
                t.$validator.validate('name')
              })
            }
            if (this._.isArray(response.data)) {
              t.worksheets.list = response.data
              t.worksheets.selected = 0
              t.worksheets.createdBy = 'connectors'
            } else {
              t.currentStatus = STATUS_SUCCESS
              t.dataset.originalFields = response.data.fields
              t.dataset.file_upload_id = response.data.file_upload_id
              t.dataset.pk_detected = response.data.pk_detected
              var item
              for (item in t.dataset.originalFields) {
                t.dataset.originalFields[item].exists = true
                t.dataset.originalFields[item].updatedName = t.dataset.originalFields[item].name
              }
              t.dataset.updatedFields = JSON.parse(JSON.stringify(t.dataset.originalFields))
            }
            t.isSchema = true
          })
            .catch(err => {
              console.log(err)
              t.$store.commit('push', { msg: 'Error ' + err.response.data.error, color: 'error' })
              t.uploadError = err.response
              t.currentStatus = STATUS_FAILED
            })
        }
      },
      // Used to add fields not stored in the backend to the connector form
      addFieldsConnector: function () {
        // We don't add fields to singer integrations
        if (this.selectedConnector.labels.includes('singer')) {
          return
        }
        this.selectedConnector.config.push({ name: 'filename', label: 'Filename', value: '', placeholder: 'Define filename', required: true })
        this.selectedConnector.config.push({ name: 'directory', label: 'Directory', value: '', placeholder: 'Define directory' })
      },
      handleClickCustomize: function (dataset) {
        // TODO: field selection here (v-dialog?)
      },
      daysOfMonth: function () {
        const arr = new Array(28).fill(undefined).map((_, i) => i < 9 ? '0' + (i + 1) : i + 1 + '')
        arr.push('-2')
        arr.push('-1')
        arr.push('00')
        return arr
      },
    },
  }
</script>
<style>
.white-font{
  color:white !important;
}
</style>
